import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useAvailableHippodromes, useBulletin} from "features/bulletin/bulletin.store";
import {
    buildMutualBettingSubTypes,
    buildMutualBettingTypes,
    bulletinProvider,
} from "features/bulletin/bulletin.service";
import {MutualBetslip} from "features/betslip/mutual.service";
import {
    asModalHtml,
    degradeMessage,
    degradeTarget,
    groupRunNos,
    mutualBreakMessage,
    mutualSelectionBreak,
} from "features/betslip/lib/selection-guard";
import {modalServiceSubject} from "services/modal.service";
import {Hippodrome} from "../../../models/bulletin.models";
import paths from "../../../paths";
import {isMobile} from "../../../App";
import {BettingTypeOptions} from "../components/betting-options";
import {BettingDataTable} from "../components/betting-datatable";
import {BettingDataTabs} from "../components/betting-tabs";
import {Possibles} from "../components/betting-possibles";
import {AGF} from "../components/betting-agf";
import {TipstersComments} from "../../home/tipsters.comments";
import {Stream} from "../../stream/stream";
import {Coupons} from "../../tipster/coupons";
import {
    NewMutualBetslip,
    NewMutualBetslipToggleTab,
    NewMutualMobileBetslipBar,
} from "./new.mutual.betslip";
import "../fob.betting/fob-betting.css";

function bettingTypesSignature(bettingTypes: any[]): string {
    return bettingTypes
        .map((el: any) => `${el.id}:${el.price}:${(el.runs ?? []).join("|")}`)
        .join(",");
}

export function BettingRoot() {
    const navigator = useNavigate();
    const hippodromes = useAvailableHippodromes();
    const bulletin = useBulletin();
    const isBulletinLoaded = Object.keys(bulletin).length > 0;

    useEffect(() => {
        bulletinProvider.refreshAvailableHippodromes();
    }, []);

    useEffect(() => {
        const activeAvailableHippodromes = hippodromes.filter((el: any) => el.wager_details.length > 0);
        if (activeAvailableHippodromes.length > 0) {
            navigator(
                `${paths.betting.url}/${activeAvailableHippodromes[0].keyword.toLowerCase()}${activeAvailableHippodromes[0]._id}`
            );
        } else if (isBulletinLoaded) {
            navigator(`${paths.home.url}`);
        }
    }, [hippodromes, isBulletinLoaded, navigator]);

    return <></>;
}

export function Betting() {
    const params: any = useParams();
    const navigator = useNavigate();

    const hippodromes = useAvailableHippodromes();

    // Tür ve alt tür TEK state'te: ayrı `useState`lerdeyken açılışta tür set edilip alt tür
    // null bırakılıyordu ve aradaki render'da bağlam "yarım kurulmuş" hâlde yayılıyordu.
    // Kapanma guard'ı o yarım hâli `run` kapanması sanıp her sayfa yenilemesinde
    // "-. koşu için bahisler kapandı" modalını basıyordu. İkisi artık hep birlikte
    // değişiyor, yani o ara hâl HİÇ oluşmuyor (eküri web'de zaten böyleydi, orada bu hata
    // hiç görülmedi).
    const [selection, setSelection] = useState<{typeId: number | null; subTypeId: number | null}>(
        {typeId: null, subTypeId: null}
    );
    const {typeId: selectedBettingTypeId, subTypeId: selectedBettingSubTypeId} = selection;
    const [isDesktopSlipOpen, setIsDesktopSlipOpen] = useState<boolean>(true);
    const [isMobileSlipOpen, setIsMobileSlipOpen] = useState<boolean>(false);

    const lastHippodromeId = useRef<any>(null);
    const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const shownBreakKey = useRef<string | null>(null);
    const dialogOpen = useRef<boolean>(false);

    const hippodrome = hippodromes.find((el: Hippodrome) => (
        el.keyword.toLowerCase() + el._id === String(params.hippodrome).toLowerCase()
    ));

    /** Bir türün ilk alt tür id'si; tür seçilirken alt tür AYNI ADIMDA belirlensin diye. */
    const firstSubTypeId = (type: any): number | null => {
        const subs = buildMutualBettingSubTypes(type);
        return subs.length > 0 ? Number(subs[0].id) : null;
    };

    const availableBettingTypes = buildMutualBettingTypes(hippodrome);
    const typesSignature = bettingTypesSignature(availableBettingTypes);
    const bettingType = availableBettingTypes.find(
        (el: any) => Number(el.id) === Number(selectedBettingTypeId)
    ) ?? null;
    const availableBettingSubTypes = buildMutualBettingSubTypes(bettingType);
    const subTypesSignature = availableBettingSubTypes.map((el: any) => `${el.id}:${el.label}`).join(",");
    const bettingSubType = availableBettingSubTypes.find(
        (el: any) => Number(el.id) === Number(selectedBettingSubTypeId)
    ) ?? null;

    useEffect(() => {
        bulletinProvider.refreshAvailableHippodromes();
    }, []);

    useEffect(() => {
        if (!hippodrome || availableBettingTypes.length === 0) {
            return;
        }
        const hippodromeChanged = lastHippodromeId.current !== hippodrome._id;
        const isStillAvailable = availableBettingTypes.some(
            (el: any) => Number(el.id) === Number(selectedBettingTypeId)
        );
        if (!hippodromeChanged && isStillAvailable) {
            return;
        }

        const preset = MutualBetslip.getOptions();
        const presetBettingType = preset.hippodrome?._id === hippodrome._id
            ? availableBettingTypes.find((el: any) => Number(el.id) === Number(preset.bettingType?.id))
            : null;
        if (presetBettingType) {
            setSelection({
                typeId: Number(presetBettingType.id),
                subTypeId: preset.bettingSubType
                    ? Number(preset.bettingSubType.id)
                    : firstSubTypeId(presetBettingType),
            });
            lastHippodromeId.current = hippodrome._id;
            return;
        }

        const sixLeg = availableBettingTypes.find((el: any) => Number(el.id) === 18);
        const pick = sixLeg ?? availableBettingTypes[0];
        setSelection({typeId: Number(pick.id), subTypeId: firstSubTypeId(pick)});
        MutualBetslip.setOptions({betSlipMultiply: 1, usePointBalance: false, complete: false, column: 1});
        lastHippodromeId.current = hippodrome._id;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hippodrome?._id, typesSignature, selectedBettingTypeId]);

    useEffect(() => {
        if (!bettingType || availableBettingSubTypes.length === 0) {
            return;
        }
        const isStillAvailable = availableBettingSubTypes.some(
            (el: any) => Number(el.id) === Number(selectedBettingSubTypeId)
        );
        if (isStillAvailable) {
            return;
        }
        setSelection((prev) => ({...prev, subTypeId: Number(availableBettingSubTypes[0].id)}));
        MutualBetslip.setOptions({column: 1});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bettingType?.id, subTypesSignature, selectedBettingSubTypeId]);

    useEffect(() => {
        if (!hippodrome) {
            return;
        }
        MutualBetslip.setOptions({
            hippodrome: hippodrome,
            bettingType: bettingType,
            bettingSubType: bettingSubType,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hippodrome?._id, typesSignature, subTypesSignature, selectedBettingTypeId, selectedBettingSubTypeId]);

    useEffect(() => {
        if (!hippodrome || !bettingType) {
            return;
        }
        try {
            // @ts-ignore
            fbq("track", "ViewContent", {
                content_name: hippodrome.hippodrome,
                content_category: bettingType.name,
                content_ids: "",
                content_type: "product",
                value: bettingType.price,
                currency: "TRY",
                event_source_url: `${paths.betting.url}/${(hippodrome.keyword + hippodrome._id).toLowerCase()}`,
            });
        } catch (e) {
            console.error(e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hippodrome?._id]);

    useEffect(() => {
        if (hippodrome) {
            return;
        }
        redirectTimer.current = setTimeout(() => {
            const firstHippodrome = hippodromes.find((el: any) => el.wager_details.length > 0);
            if (firstHippodrome) {
                navigator(`${paths.betting.url}/${firstHippodrome.keyword.toLowerCase()}${firstHippodrome._id}`);
            }
        }, 3000);
        return () => {
            if (redirectTimer.current) {
                clearTimeout(redirectTimer.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hippodrome?._id, params.hippodrome]);

    // Bülten tick'inde seçim bağlamı kırıldıysa: tek düğmeli modal, onayında kuponun bu
    // bağlamdaki satırlarını temizle ve sayfayı AÇILIŞ yolundan yeniden kur (BettingRoot
    // zaten "açık olan ilk kartı seç" mantığını taşıyor). Müşterekte sessiz taşıma
    // YAPILMIYOR - kupon (kart+tür+alt tür) bağlamına kilitli olduğu için taşımak
    // seçimleri silmeden görünmez yapardı.
    useEffect(() => {
        if (dialogOpen.current) {
            return;
        }
        const options = MutualBetslip.getOptions();
        // Gönderim uçuştayken bağlam değiştirilmez; kupon gidene kadar bekler.
        // Alt tür de ŞART: sayfa açılışta türü set edip alt türü null'a çekiyor, seçimi ayrı
        // bir effect yapıyor. O aradaki render'da bağlam "kurulmamış" hâlde ve null alt tür
        // `run` kırılması gibi görünüyordu - her sayfa yenilemesinde "-. koşu için bahisler
        // kapandı" modalı çıkıyordu. Null alt tür burada her zaman GEÇİCİ bir açılış hâli;
        // gerçek kapanmada alt tür eski değerinde kalır ve kırılma yine yakalanır.
        if (!options.hippodrome ||
            !options.bettingType ||
            !options.bettingSubType ||
            options.inProgress) {
            return;
        }
        const brk = mutualSelectionBreak({
            card: options.hippodrome,
            availableCards: hippodromes,
            typeId: options.bettingType?.id,
            subTypeId: options.bettingSubType?.id,
        });
        if (brk === "ok") {
            shownBreakKey.current = null;
            return;
        }
        // Aynı kırılma için BİR modal: tick saniyede bir aynı şeyi bildiriyor.
        const key = `${brk}:${options.hippodrome?._id}:${options.bettingType?.id}:${options.bettingSubType?.id}`;
        if (shownBreakKey.current === key) {
            return;
        }
        shownBreakKey.current = key;
        dialogOpen.current = true;

        const cleared = MutualBetslip.getCurrentItems().length;

        // Önce ZİNCİR denenir: ayak kapandığında kullanıcıyı bültenin dışına atmak yerine bir
        // alt basamağa bırakıyoruz (7'li → 6'lı → ... → Çifte → Ganyan). Kupon her iki yolda
        // da boşalıyor; fark yalnız nereye düştüğü. Hedef yoksa aşağıdaki sıfırlama yolu.
        const chainTarget = degradeTarget({
            brk,
            card: options.hippodrome,
            currentType: options.bettingType,
            groupRuns: groupRunNos(options.bettingSubType?.label),
        });
        if (chainTarget) {
            const fromName = options.bettingType?.name ?? "";
            MutualBetslip.clear();
            setSelection({
                typeId: Number(chainTarget.type.id),
                subTypeId: Number(chainTarget.subTypeId),
            });
            MutualBetslip.setOptions({betSlipMultiply: 1, complete: false, column: 1});
            modalServiceSubject.next({
                title: "Bahisler kapandı",
                case: "warning",
                content: asModalHtml(degradeMessage({
                    closedRunNo: chainTarget.closedRunNo,
                    fromTypeName: fromName,
                    toTypeName: chainTarget.type.name,
                    clearedCount: cleared,
                })),
                onClose: () => {
                    dialogOpen.current = false;
                },
                confirm: {
                    cancel: {
                        label: "Tamam",
                        action: () => {
                            dialogOpen.current = false;
                        },
                    },
                },
            });
            return;
        }
        let handled = false;
        const resume = () => {
            if (handled) return;
            handled = true;
            dialogOpen.current = false;
            MutualBetslip.clear();
            if (brk === "card") {
                // Kartın kendisi düştü: kök rotaya dön, açılış yolu ilk açık kartı seçsin.
                navigator(paths.betting.url);
                return;
            }
            setSelection({typeId: null, subTypeId: null});
        };

        modalServiceSubject.next({
            title: "Bahisler kapandı",
            case: "warning",
            content: asModalHtml(mutualBreakMessage({
                brk,
                cardName: options.hippodrome?.hippodrome,
                typeName: options.bettingType?.name,
                subTypeId: options.bettingSubType?.id,
                clearedCount: cleared,
            })),
            onClose: resume,
            confirm: {cancel: {label: "Tamam", action: resume}},
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hippodromes, typesSignature, subTypesSignature, selectedBettingTypeId, selectedBettingSubTypeId]);

    useEffect(() => {
        setIsMobileSlipOpen(false);
    }, [params.subpage, params.hippodrome]);

    const applyBettingSelection = (typeId: any, subTypeId: any, column: number = 1) => {
        if (!hippodrome) {
            return;
        }
        const nextType = availableBettingTypes.find((el: any) => Number(el.id) === Number(typeId)) ?? null;
        const nextSubTypes = buildMutualBettingSubTypes(nextType);
        const nextSubType = nextSubTypes.find((el: any) => Number(el.id) === Number(subTypeId)) ?? nextSubTypes[0] ?? null;

        const selectionChanged =
            Number(nextType?.id) !== Number(selectedBettingTypeId) ||
            Number(nextSubType?.id) !== Number(selectedBettingSubTypeId);

        setSelection({
            typeId: nextType ? Number(nextType.id) : null,
            subTypeId: nextSubType ? Number(nextSubType.id) : null,
        });
        MutualBetslip.setOptions({
            hippodrome: hippodrome,
            bettingType: nextType,
            bettingSubType: nextSubType,
            column: column,
            ...(selectionChanged ? {betSlipMultiply: 1, complete: false} : {}),
        });
    };

    const selectBettingColumn = (no: any) => {
        MutualBetslip.setOptions({column: Number(no)});
    };

    if (!hippodrome) {
        return <></>;
    }

    const hasOpenWagers = hippodrome.wager_details.length > 0;
    const isSlipVisible = hasOpenWagers && !!bettingType && bettingType.id !== 0 && !!bettingSubType;
    const dataColumnClass = !isMobile && isSlipVisible && isDesktopSlipOpen ? "col-data" : "col-12";

    const betslipBlock = (
        <>
            {isSlipVisible && !isMobile && isDesktopSlipOpen && (
                <div className="col-betslip">
                    <NewMutualBetslip onClose={() => setIsDesktopSlipOpen(false)}/>
                </div>
            )}

            {isSlipVisible && !isMobile && !isDesktopSlipOpen && (
                <NewMutualBetslipToggleTab onOpen={() => setIsDesktopSlipOpen(true)}/>
            )}

            {isSlipVisible && isMobile && !isMobileSlipOpen && (
                <NewMutualMobileBetslipBar onOpen={() => setIsMobileSlipOpen(true)}/>
            )}

            {isSlipVisible && isMobile && isMobileSlipOpen && (
                <div className="col-betslip">
                    <NewMutualBetslip onClose={() => setIsMobileSlipOpen(false)}/>
                </div>
            )}
        </>
    );

    return (
        <>
            <BettingTypeOptions
                params={params}
                hippodrome={hippodrome}
                hippodromes={hippodromes}
                bettingType={bettingType}
                bettingSubType={bettingSubType}
                availableBettingTypes={availableBettingTypes}
                availableBettingSubTypes={availableBettingSubTypes}
                isMobileSlipOpen={isMobileSlipOpen}
                onChangeHippodrome={(value: string) => navigator(`${paths.betting.url}/${value.toLowerCase()}`)}
                selectBettingType={(id: any) => applyBettingSelection(id, null, 1)}
                selectBettingSubType={(id: any) => applyBettingSelection(selectedBettingTypeId, id, 1)}
            />

            <div className="row">
                {!params.subpage && (
                    <>
                        {!(isMobile && isMobileSlipOpen) && (
                            <div className={dataColumnClass}>
                                {hasOpenWagers && (
                                    <div className="page-widget">
                                        <svg style={{display: "none"}}>
                                            <defs>
                                                <path id="tabshape" d="M80,60C34,53.5,64.417,0,0,0v60H80z"></path>
                                            </defs>
                                        </svg>
                                        <BettingDataTabs selectBettingColumn={selectBettingColumn}/>

                                        <BettingDataTable/>
                                    </div>
                                )}

                                {!hasOpenWagers && (
                                    <div className={"page-widget"}>
                                        <div className="hint-text text-center p-3" style={{minHeight: "50vh"}}>
                                            <p className={"fs-6 mb-0"}>Bu hipodromda açık koşu bulunmamaktadır.</p>
                                        </div>
                                    </div>
                                )}

                                {hasOpenWagers && <Hints bettingType={bettingType}/>}

                                {hasOpenWagers && (
                                    <div className={"page-widget mt-4"}>
                                        <div className="hint-text p-3">
                                            <p>
                                                Bu sayfadaki veriler United Racing ve üçüncü taraf tedarikçi/veri
                                                sağlayıcılardan temin edilmiştir,
                                                bu bilgilerin doğruluğundan 4nala.com sorumlu değildir.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {betslipBlock}
                    </>
                )}

                {params.subpage === "muhtemeller" && (
                    <>
                        {!(isMobile && isMobileSlipOpen) && (
                            <div className={dataColumnClass}>
                                <Possibles
                                    hippodrome={hippodrome}
                                    availableBettingTypes={availableBettingTypes}
                                    onSelectBetting={applyBettingSelection}
                                />
                            </div>
                        )}

                        {betslipBlock}
                    </>
                )}

                {params.subpage === "agf" && (
                    <>
                        {!(isMobile && isMobileSlipOpen) && (
                            <div className={dataColumnClass}>
                                <AGF
                                    hippodrome={hippodrome}
                                    availableBettingTypes={availableBettingTypes}
                                    onSelectBetting={applyBettingSelection}
                                />
                            </div>
                        )}

                        {betslipBlock}
                    </>
                )}

                {params.subpage === "yorumlar" && (
                    <div className="col-12">
                        <div className="page-widget">
                            <svg style={{display: "none"}}>
                                <defs>
                                    <path id="tabshape" d="M80,60C34,53.5,64.417,0,0,0v60H80z"></path>
                                </defs>
                            </svg>
                            <ul className="nav nav-tabs">
                                <li className="nav-item active">
                                    <svg className="left" viewBox="0 0 80 60" preserveAspectRatio="none">
                                        <use xlinkHref="#tabshape"></use>
                                    </svg>
                                    <a className="nav-link">UZMAN YORUMLARI</a>
                                    <svg viewBox="0 0 80 60" preserveAspectRatio="none">
                                        <use xlinkHref="#tabshape"></use>
                                    </svg>
                                </li>
                            </ul>

                            <div className="p-3">
                                <TipstersComments hippodrome={hippodrome}></TipstersComments>
                            </div>
                        </div>
                    </div>
                )}

                {params.subpage === "hazir-kuponlar" && (
                    <div className="col-12">
                        <div className="page-widget">
                            <Coupons hippodrome={hippodrome}></Coupons>
                        </div>
                    </div>
                )}

                {params.subpage === "canli-tv" && (
                    <div className="col-12">
                        <Stream/>
                    </div>
                )}
            </div>
        </>
    );
}

export function Hints({bettingType}: any) {
    const [showHist, setShowHint] = useState<boolean>(false);
    const [selectedTab, setSelectedTab] = useState(0);

    useEffect(() => {
        if (!bettingType || bettingType.isPlaceholder) {
            setSelectedTab(1);
        } else {
            setSelectedTab(0);
        }
    }, [bettingType?.id]);

    const hasHowTo = !!bettingType && bettingType.id !== 0;

    return (
        <div className="page-widget mt-4">
            <ul className="nav nav-tabs">
                {hasHowTo && <li className={`nav-item ${selectedTab === 0 ? "active" : ""}`}
                                 onClick={() => setSelectedTab(0)}>
                    <svg className="left" viewBox="0 0 80 60" preserveAspectRatio="none">
                        <use xlinkHref="#tabshape"></use>
                    </svg>
                    <a className="nav-link">Nasıl Oynanır</a>
                    <svg viewBox="0 0 80 60" preserveAspectRatio="none">
                        <use xlinkHref="#tabshape"></use>
                    </svg>
                </li>}
                <li className={`nav-item ${selectedTab === 1 ? "active" : ""}`} onClick={() => setSelectedTab(1)}>
                    <svg className="left" viewBox="0 0 80 60" preserveAspectRatio="none">
                        <use xlinkHref="#tabshape"></use>
                    </svg>
                    <a className="nav-link">YARDIM</a>
                    <svg viewBox="0 0 80 60" preserveAspectRatio="none">
                        <use xlinkHref="#tabshape"></use>
                    </svg>
                </li>
            </ul>

            {selectedTab === 0 && hasHowTo && (
                <div className="hint-text p-3">
                    <h3>{bettingType.name} nasıl oynanır?</h3>
                    <p>{bettingType.info}</p>
                </div>
            )}
            {selectedTab === 1 && (
                <div className="hint-text p-3">
                    <h3>At Yarışı Nedir? At Yarışı Oyunu Nasıl Oynanır?</h3>
                    <p>
                        At Yarışı, United Racing tarafından düzenlenen ve resmi yarış programına göre
                        oynatılan
                        müşterek bir bahis oyunudur. Bu oyun, yarış sonuçlarının tahmin edilmesi üzerine kuruludur.
                        Tarihi oldukça
                        eski dönemlere dayanan bu oyun, hem eğlence hem de bahis amaçlı oynanır. Bu yazıda, at yarışının
                        ne
                        olduğunu, tarihini, bahis türlerini, altılı sonuçları ve altılı ganyanın nasıl oynandığını
                        ayrıntılı olarak
                        ele alacağız.
                    </p>
                    {!showHist && (
                        <a
                            className="cursor-pointer link"
                            onClick={() => {
                                setShowHint(true);
                            }}
                        >
                            Devamını Oku
                        </a>
                    )}

                    {showHist && (
                        <>
                            <h3>At Yarışı Nedir? </h3>
                            <p>
                                Gazi Mustafa Kemal Atatürk'ün ifade ettiği gibi, at yarışı modern toplumlar için bir
                                sosyal ihtiyaçtır.
                                Peki, at yarışı nedir ve nasıl bu kadar popüler hale gelmiştir? At yarışı, insanlık
                                tarihinin bilinen en
                                eski spor dallarından biri olarak kabul edilmektedir.
                            </p>
                            <p>
                                At yarışları, 1750 yılında İngiltere'de Newmarket'ta kurulan Jokey Kulübü tarafından
                                organize edilmeye
                                başlanmıştır.
                            </p>

                            <p>
                                Fransa'da, Jokey Kulübü Büyük Ödülü, Paris Büyük Ödülü ve Zafer Takı Büyük Ödülü gibi
                                prestijli yarışlar
                                düzenlenmektedir. Bu yarışlar, Fransa'nın at yarışı geleneğinde önemli bir yer tutar ve
                                uluslararası
                                alanda büyük ilgi görmektedir. Avustralya'da ise en ünlü yarış Melbourne Kupa
                                Koşusu'dur. Dünya
                                Binicilik Şampiyonası ise ilk kez 1970 yılında düzenlenmiştir.
                            </p>
                            <p>
                                At yarışlarının daha heyecan verici bir spor haline gelmesinde etkili olan faktörlerden
                                biri de
                                bahislerdir. At yarışı, resmi programlar dahilinde oynanan ve oldukça heyecan verici bir
                                bahis oyunudur.
                                Önceden belirlenmiş olan ikramiyeler, başarılı bahis yapan kişiler arasında
                                bölüştürülür. Atların koşu
                                stillerini tanımak, oluşturulan bahis kuponunun başarılı olmasını sağlayabilir.
                            </p>
                            <p>
                                At yarışı, düşük bütçelerle bile oynanabilen bir oyun olup, küçük bir miktarla büyük
                                ödüller kazanma
                                imkanı sunduğundan çekiciliğini korumaktadır. Bu yazıda, at yarışı bahis türleri ve
                                altılı yarış
                                tahminleri gibi konulara da değineceğiz.
                            </p>
                            <h3>At Yarışında Bahis Türleri</h3>
                            <p>
                                Türkiye'de at yarışı, oldukça yaygın bir etkinlik olup, bahis oyunları arasında önemli
                                bir yer
                                tutmaktadır. United Racing tarafından düzenlenen yarış programları
                                doğrultusunda, en çok
                                oynanan bahis türü altılı ganyandır. Bunun yanı sıra, at yarışı kuponları bakımından
                                birçok alternatif
                                bahis türü bulunmaktadır.
                            </p>

                            <p>
                                At yarış programı, at yarışı bahis türleri açısından önemli bir kılavuz niteliğindedir.
                                Altılı yarış
                                tahmini, bu oyunun en önemli parçası olmasına rağmen, diğer bahis türleri de bahisçilere
                                çeşitli kazanç
                                fırsatları sunmaktadır.
                            </p>

                            <p>
                                Oyunlar arasında 5'li Ganyan, 4'lü Ganyan, 3'lü Ganyan, Ganyan, Çifte Bahis, Sıralı
                                İkili, Sırasız
                                İkili, Sıralı Üçlü Bahis, Tabela Bahis, Tabela Bahis (Virgüllü), Plase, 7'li Plase,
                                Plase 2'li, Sıralı
                                Beşli Bahis, Sıralı Beşli Bahis (Virgüllü), Ganyan & Plase bulunmaktadır. Tüm oyunlardan
                                en düşük bahis
                                tutar 20 TL olarak United Racing tarafından belirlenen bir kuraldır.
                            </p>

                            <h3>At Yarışının Temel Kuralları</h3>
                            <p>
                                At yarışlarının temel kuralları oldukça kapsamlı olup, bu alanda bahis yapmak teknik
                                bilgi birikimi
                                gerektirir. Yarış terimlerini ve anlamlarını iyi bilmek, kısaltmaları doğru takip etmek
                                bu süreçte büyük
                                önem taşır. United Racing tarafından çıkarılan resmi programa göre oynanan
                                at yarışları,
                                müşterek bahisler için belirlenen kurallara tabidir. Daha ayrıntılı bilgiler, at yarışı
                                genel hükümleri
                                ve her yıl güncellenen at yarışı genel yönetmeliklerinde yer almaktadır.
                            </p>

                            <h3>At Yarışı Nereden Oynanır?</h3>
                            <p>
                                At yarışı kupon yapmak günümüzde en ideal yöntem sanal bayilerdir. 4nala.com'dan yasal
                                ve güvenilir
                                şekilde at yarışı bahislerinizi oynayabilirsiniz.
                            </p>

                            <h3>At Yarışı Tahmini Nasıl Yapılır?</h3>
                            <p>
                                At yarışı tahmini ve altılı hazır kupon oluşturma, teknik bilgi ve dikkat gerektiren bir
                                süreçtir.
                                4nala.com, geniş uzman yazar kadrosuyla altılı yarış tahminleri konusunda yarışseverlere
                                kapsamlı
                                bilgiler sunmaktadır. At yarışlarının karakteristik özelliklerinden biri, sürpriz
                                atların beklenmedik
                                başarılarıdır; bu nedenle, tahmin yaparken sürpriz atlara odaklanmak önemlidir.
                            </p>

                            <h3>At Yarışı Oyunlarında Taktikler ve İpuçlarının Önemi</h3>

                            <p>
                                At yarışı oyunlarında başarı için taktikler ve ipuçları büyük önem taşır. Banko, plase
                                ve sürpriz
                                yapacak atları belirlemek, oyuncuların kazanma şansını artıran kritik bir stratejidir.
                                Bu noktada,
                                4nala.com'un uzman yazarlarını takip etmek en kolay ve etkili yöntemdir.
                            </p>

                            <p>
                                Bahis kuponlarına eklenen atların önceki performansları titizlikle incelenmelidir. Atın
                                son beş yarışı
                                değil, genel performansı dikkate alınmalıdır. Ne kadar doğru analiz yapılırsa yapılsın,
                                bahis yaparken
                                bütçeyi aşmamak önemlidir. Bilinçli bir bütçe yönetimi, uzun vadede başarıyı sürdürmenin
                                anahtarıdır.
                            </p>
                            <p>
                                At yarışı oyunlarında başarı için taktikler ve ipuçlarını dikkate almak, oyuncuların
                                kazanma şansını
                                artırır ve daha bilinçli bahisler yapmalarını sağlar. Bu nedenle, at yarışı
                                tahminlerinde detaylı bir
                                analiz ve stratejik bir yaklaşım önemlidir.
                            </p>
                            <h3>At Yarışı Sonuçları Nereden Takip Edilir?</h3>
                            <p>
                                At yarışı resmi sonuçları ve Altılı Ganyan sonuçları, United Racing
                                tarafından
                                açıklanmaktadır. 4nala.com üzerinden de Altılı Ganyan sonuçları takip edilebilmektedir.
                                Altılı Ganyan
                                sonuçları, son ayak koşulduktan sonra genellikle 20-25 dakika içinde duyurulmaktadır.
                            </p>
                            <h3>Altılı Ganyan Sonuçları</h3>
                            <p>
                                Altılı Ganyan sonuçları, yarışseverlerin en çok merak ettiği konulardan biridir.  bu
                                sonuçları canlı
                                olarak duyurur ve kazananların ne kadar kazandığı ve dağıtılacak miktarlar hakkında
                                bilgi sağlar. Eğer
                                Altılı Ganyan'da kazanan çıkmazsa ve ikramiye devredilirse, toplanan tevzi miktarı
                                tarafından
                                belirlenen başka bir yarış programına aktarılmaktadır.
                            </p>
                            <h3>Sonuçların Takibi</h3>
                            <p>
                                At yarışı sonuçlarını anlık olarak 4nala.com'dan takip edebilirsiniz. Bu platform,
                                yarışseverlere hızlı
                                ve güvenilir bilgi akışı sağlayarak, bahislerin sonuçlarını anında öğrenme imkanı
                                sunmaktadır.
                            </p>
                            <p>
                                Sonuç olarak, at yarışı ve Altılı Ganyan sonuçlarını takip etmek isteyen yarışseverler,
                                resmi
                                duyurularını ve 4nala.com'u kullanarak güncel bilgilere ulaşabilirler. Bu sayede, yarış
                                sonuçları ve
                                kazançlar hakkında anında bilgi sahibi olabilirsiniz.
                            </p>
                            {showHist && (
                                <a
                                    className="cursor-pointer link"
                                    onClick={() => {
                                        setShowHint(false);
                                    }}
                                >
                                    Gizle
                                </a>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
